import React, { useState, useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { useUserStore } from "../../store";

function AmiliActivationDialog({ open, onClose, onSuccess, t }) {
  const { user, currentOrganization } = useUserStore();
  const organizationId = currentOrganization?.organization_id;

  const [contactPerson, setContactPerson] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [customerServiceEmail, setCustomerServiceEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [customerServicePhone, setCustomerServicePhone] = useState("");
  const [billingStreet, setBillingStreet] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingZip, setBillingZip] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);
  const [bicMissing, setBicMissing] = useState(false);

  useEffect(() => {
    if (open && organizationId) {
      setLoadingDetails(true);
      setError(null);

      axios
        .get(`/api/organizations/${organizationId}/`)
        .then((response) => {
          const org = response.data;
          setBicMissing(!org.bic);
          setContactPerson(user?.name || "");
          setContactEmail(user?.email || "");
          setCustomerServiceEmail(org.email || user?.email || "");
          setContactPhone(org.phone || "");
          setCustomerServicePhone(org.phone || "");
          setBillingStreet(org.address_street || "");
          setBillingCity(org.address_city || "");
          setBillingZip(org.address_postal_code || "");
        })
        .catch((err) => {
          console.error("Failed to fetch organization details:", err);
          setError(t("Failed to load organization details"));
        })
        .finally(() => {
          setLoadingDetails(false);
        });
    }
  }, [open, organizationId, user?.email, user?.name, t]);

  const handleActivate = async () => {
    if (!contactPerson || !contactEmail || !customerServiceEmail || !contactPhone) {
      setError(t("amili.allFieldsRequired", "Kaikki kentät ovat pakollisia"));
      return;
    }
    if (!billingStreet || !billingCity || !billingZip) {
      setError(t("amili.addressRequired", "Laskutusosoite on pakollinen"));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(
        `/api/organizations/${organizationId}/amili/activate/`,
        {
          contact_person: contactPerson,
          contact_email: contactEmail,
          customer_service_email: customerServiceEmail,
          contact_phone_number: contactPhone,
          customer_service_phone_number: customerServicePhone || undefined,
          billing_street: billingStreet,
          billing_city: billingCity,
          billing_zip: billingZip,
        }
      );
      onSuccess(response.data);
      onClose();
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || t("amili.activationFailed", "Aktivointi epäonnistui");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t("amili.activateTitle", "Aktivoi maksuvalvonta")}</DialogTitle>
      <DialogContent>
        {loadingDetails ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t(
                "amili.activateDescription",
                "Amili Kassavirta seuraa automaattisesti laskujesi maksuliikennettä ja hoitaa maksumuistutukset sekä perinnän puolestasi."
              )}
            </Typography>

            {bicMissing && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {t(
                  "amili.bicRequired",
                  "BIC-koodi puuttuu. Aseta BIC-koodi laskutusasetuksissa ennen maksuvalvonnan aktivointia."
                )}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t("amili.contactInfo", "Yhteystiedot")}
            </Typography>

            <TextField
              margin="dense"
              label={t("Contact Person")}
              fullWidth
              value={contactPerson}
              onChange={(e) => setContactPerson(e.target.value)}
              required
              sx={{ mb: 1 }}
            />
            <TextField
              margin="dense"
              label={t("amili.contactEmail", "Yhteyshenkilön sähköposti")}
              type="email"
              fullWidth
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
              required
              sx={{ mb: 1 }}
            />
            <TextField
              margin="dense"
              label={t("Phone Number")}
              fullWidth
              value={contactPhone}
              onChange={(e) => setContactPhone(e.target.value)}
              required
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t("amili.customerServiceInfo", "Asiakaspalvelun tiedot")}
            </Typography>

            <TextField
              margin="dense"
              label={t("amili.customerServiceEmail", "Asiakaspalvelun sähköposti")}
              type="email"
              fullWidth
              value={customerServiceEmail}
              onChange={(e) => setCustomerServiceEmail(e.target.value)}
              required
              sx={{ mb: 1 }}
            />
            <TextField
              margin="dense"
              label={t("amili.customerServicePhone", "Asiakaspalvelun puhelinnumero")}
              fullWidth
              value={customerServicePhone}
              onChange={(e) => setCustomerServicePhone(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t("amili.billingAddress", "Laskutusosoite")}
            </Typography>

            <TextField
              margin="dense"
              label={t("Street Address")}
              fullWidth
              value={billingStreet}
              onChange={(e) => setBillingStreet(e.target.value)}
              required
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: "flex", gap: 2 }}>
              <TextField
                margin="dense"
                label={t("Postal Code")}
                value={billingZip}
                onChange={(e) => setBillingZip(e.target.value)}
                required
                sx={{ width: 150 }}
              />
              <TextField
                margin="dense"
                label={t("City")}
                fullWidth
                value={billingCity}
                onChange={(e) => setBillingCity(e.target.value)}
                required
              />
            </Box>

            <Alert severity="info" sx={{ mt: 2 }}>
              {t(
                "amili.activateNote",
                "Aktivoinnin jälkeen saat sähköpostiisi KYC-tunnistautumislinkin. Palvelu aktivoituu tunnistautumisen jälkeen."
              )}
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {t("Cancel")}
        </Button>
        <Button
          variant="contained"
          onClick={handleActivate}
          disabled={loading || loadingDetails || bicMissing}
        >
          {loading ? <CircularProgress size={24} /> : t("amili.activate", "Aktivoi")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default withTranslation()(AmiliActivationDialog);
